import { getModeByName, Mode, ModeCategory, ModeName, modesByCategory } from '@commutifi/modes'
import cx from 'classnames'
import { CombinedModes } from '@commutifi-fe/constants'
import { FormattedMessage, useIntl } from 'locales/index'
import findKey from 'lodash/findKey'
import React, { ReactNode, Suspense, useState } from 'react'
import {
  Dropdown,
  DropDownProps,
  MenuProps,
  Typography,
  SelectableCardGrid,
  CardGridSize,
  DropdownProps,
  Skeleton
} from '@commutifi-fe/ui'
import { colors } from '@commutifi-fe/ui/styles/constants'
import { FormItemInjectedProps } from 'shared/interfaces'
import { CommuteTransport } from 'shared/interfaces/Commute'
import {
  displayModes,
  getModeIntl,
  getSurveySelectionModesTranslationKeys,
  getTranslationKeyForCategory
} from 'utils/modes'
import { ModeCardsSelectorContext, useModeCardsSelectorContext } from './Context'
import styles from './styles.module.scss'

const BikeIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Bike'))
const CarIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Car'))
const CustomCommuteIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/CustomCommute'))
const PublicTransitIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/PublicTransit'))
const SeatsIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Seats'))
const SwirlIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Swirl'))
const WalkIllustration = React.lazy(() => import('@commutifi-fe/custom-icons/svgs/Walk'))

const { ClickableCard } = SelectableCardGrid
const { Text } = Typography

interface ModeData {
  mode: ModeName
  category: ModeCategory
  title: React.ReactNode
  description?: React.ReactNode
}
interface TransportOption {
  category: ModeCategory
  label: React.ReactNode
  img: React.ReactNode
  modes: ModeData[]
}

interface IModesOptionsPopoverProps extends DropDownProps {
  transportOption: TransportOption
  onSelect: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, mode: ModeName) => void
  children: React.ReactElement
}

interface IModeCardsSelectorProps extends FormItemInjectedProps<CommuteTransport> {
  cardSize?: `${CardGridSize}`
  equalWidth?: boolean
  baseWidth?: number | 'auto'
  className?: string
  /**
   * The number of options for each mode card will be displayed when this property is true
   * since the user can't interact with the cards to see the options in some cases like the survey builder
   * @example Set to true in survey builder to show the component but allow 0 interactions with it
   */
  showDropdownOptionsCount?: boolean
  children?: React.ReactElement | React.ReactElement[]
}

const cardButtonStyle = { minHeight: '44px' }

function ModeOptionsCount({ count }: { count: number }) {
  const intl = useIntl()
  return (
    <Text id="mode-selector-options-count" type="secondary" className="u-margin-top--tiny">
      {intl.formatMessage({ id: 'global.dropdown.options.count' }, { count: count || 1 })}
    </Text>
  )
}

function ModeOptions({
  children,
  modes
}: { children: ReactNode; modes?: undefined } | { modes: ReactNode; children?: undefined }) {
  const intl = useIntl()
  return (
    <Text id="mode-selector-options-count" type="secondary" className="u-margin-top--tiny">
      {children || intl.formatMessage({ id: 'surveys.questions.types.commuteRouter.options.description' }, { modes })}
    </Text>
  )
}

function ModesOptionsPopover({
  transportOption,
  onSelect,
  children,
  onOpenChange,
  ...rest
}: IModesOptionsPopoverProps) {
  const [open, setOpen] = useState(false)

  const handleOpenChange: DropdownProps['onOpenChange'] = (open, info) => {
    setOpen(open)
    onOpenChange?.(open, info)
  }

  const handleSelect: MenuProps['onSelect'] = ({ domEvent, key: mode }) => {
    if (onSelect) {
      onSelect(domEvent, mode as ModeName)
    }
    setOpen(false)
    onOpenChange?.(false, { source: 'trigger' })
  }

  const handleMenuKeyDown: MenuProps['onKeyDown'] = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      setOpen(false)
      onOpenChange?.(false, { source: 'trigger' })
    }
  }

  const items = transportOption.modes.map((mode) => ({
    key: mode.mode,
    label: (
      <div>
        <Text size="lead" strong="medium" block>
          {mode.title}
        </Text>
        <span>{mode.description}</span>
      </div>
    )
  }))

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        onSelect: handleSelect,
        onKeyDown: handleMenuKeyDown,
        // @ts-expect-error -- It does exist
        'data-testid': 'mode-dropdown-menu'
      }}
      trigger={['click']}
      arrow
      open={open}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      {children}
    </Dropdown>
  )
}

const modesSectionMapping: { [key in ModeCategory]: Mode[] } = {
  ...modesByCategory,
  // We do some customizations on the mapping we get from the lib. We basically add carpool options to the right place
  // and set public transit to have no modes (cause we won't ask the user for the specifics)
  [ModeCategory.Drive]: [
    modesByCategory[ModeCategory.Drive][0],
    getModeByName(ModeName.CarpoolDriver),
    ...modesByCategory[ModeCategory.Drive].slice(1, modesByCategory[ModeCategory.Drive].length)
  ],
  [ModeCategory.PublicTransit]: [],
  [ModeCategory.SharedRide]: [
    getModeByName(ModeName.CarpoolPassenger),
    ...modesByCategory[ModeCategory.SharedRide].filter((mode) => mode.name !== ModeName.Taxi)
  ]
}

function ModeCardsSelector({
  id,
  onChange,
  cardSize = CardGridSize.Default,
  value,
  equalWidth = true,
  baseWidth,
  className,
  children,
  showDropdownOptionsCount = false
}: IModeCardsSelectorProps) {
  const getTransitOptionsForCategory = (category: ModeCategory) =>
    modesSectionMapping[category].map((mode): ModeData => {
      const { title, description } = getSurveySelectionModesTranslationKeys(mode.name)
      return {
        category: mode.category,
        mode: mode.name,
        title: <FormattedMessage id={title} defaultMessage="—" />,
        description: description ? <FormattedMessage id={description} defaultMessage="—" /> : undefined
      }
    })

  const transportOptions: TransportOption[] = [
    {
      category: ModeCategory.Drive,
      label: <FormattedMessage id={getTranslationKeyForCategory(ModeCategory.Drive).title} />,
      img: <CarIllustration height={54} />,
      modes: getTransitOptionsForCategory(ModeCategory.Drive)
    },
    {
      category: ModeCategory.SharedRide,
      label: <FormattedMessage id={getTranslationKeyForCategory(ModeCategory.SharedRide).title} />,
      img: <SeatsIllustration height={72} />,
      modes: getTransitOptionsForCategory(ModeCategory.SharedRide)
    },
    {
      category: ModeCategory.PublicTransit,
      label: (
        <FormattedMessage id={getTranslationKeyForCategory(ModeCategory.PublicTransit).title} defaultMessage="—" />
      ),
      img: <PublicTransitIllustration height={72} />,
      modes: getTransitOptionsForCategory(ModeCategory.PublicTransit)
    },
    {
      category: ModeCategory.Bike,
      label: <FormattedMessage id={getTranslationKeyForCategory(ModeCategory.Bike).title} />,
      img: <BikeIllustration fill={colors.primary300} height={72} />,
      modes: getTransitOptionsForCategory(ModeCategory.Bike)
    },
    {
      category: ModeCategory.Walk,
      label: <FormattedMessage id={getTranslationKeyForCategory(ModeCategory.Walk).title} />,
      img: <WalkIllustration height={72} />,
      // We only have one walking mode in this category so we will select it by default
      modes: getTransitOptionsForCategory(ModeCategory.Walk)
    }
  ]

  const [selectedMode, setSelectedMode] = useState<CommuteTransport | null>(null)
  const currentCategory = value?.category || selectedMode?.category
  const currentMode = value?.mode || selectedMode?.mode

  const handleTransportChange = React.useCallback(
    (category: ModeCategory, mode?: ModeName) => {
      if (onChange) {
        onChange({ category, mode })
      }
    },
    [onChange]
  )

  const handleSelectCategoryAndMode = (
    e: React.MouseEvent | React.KeyboardEvent,
    { category: categoryParam, mode: modeParam }: CommuteTransport
  ) => {
    e.preventDefault()
    // If we have a mode name we try to fetch the full mode because we should give priority to the mode category
    // The params could have category: drive (the section that the user selected) and mode Carpool
    // In this case we want to fetch the full carpool mode to get the appropriate category
    const { category, name: mode } = getModeByName(modeParam || '') || {}
    setSelectedMode({ category, mode })
    handleTransportChange(category || categoryParam, mode)
  }

  const handleSelectCategory = React.useCallback(
    (e: React.MouseEvent, category: ModeCategory) => {
      e.preventDefault()
      setSelectedMode({ category, mode: undefined })
      handleTransportChange(category)
    },
    [handleTransportChange]
  )

  const modeCardRef = React.useRef<HTMLDivElement | null>(null)

  const intl = useIntl()
  const renderButtonCard = (
    transportOption: TransportOption,
    extraButtonProps?: { onClick: (e: React.MouseEvent, category: ModeCategory) => void }
  ) => (
    <div key={transportOption.category} className="u-flex-column u-flex-1">
      <ClickableCard
        ref={modeCardRef}
        id={transportOption.category}
        cover={
          <span className="u-margin--medium u-margin-bottom--none">
            <Suspense fallback={<Skeleton.Image style={{ height: '72px', width: '115px' }} />}>
              {transportOption.img}
            </Suspense>
          </span>
        }
        contentStyle={cardButtonStyle}
        title={transportOption.label}
        description={
          // If there is only one mode in the category we don't need to display
          // a subtitle on the card
          currentMode &&
          transportOption.modes &&
          transportOption.modes.length > 1 &&
          transportOption.modes.find((option) => option.mode === currentMode) ? (
            <span className="u-font-weight-semi">
              <FormattedMessage id={getSurveySelectionModesTranslationKeys(currentMode).title} defaultMessage="—" />
            </span>
          ) : null
        }
        {...extraButtonProps}
      />
      {showDropdownOptionsCount &&
        (transportOption.category === ModeCategory.PublicTransit ? (
          <ModeOptions>
            {intl.formatMessage({ id: 'surveys.questions.types.commuteRouter.options.description.publicTransitOnly' })}
          </ModeOptions>
        ) : transportOption.modes.length > 1 ? (
          <ModeOptions
            modes={displayModes(
              transportOption.modes.map((mode) => mode.mode),
              intl,
              {
                hideIcon: true
              }
            )}
          />
        ) : (
          <Text type="secondary">
            <ModeOptionsCount count={1} /> - {intl.formatMessage(getModeIntl(transportOption.modes[0].mode))}
          </Text>
        ))}
    </div>
  )
  // Case 1: Render a simple checkbox button to select the mode category (ex. public transit, walk)
  // Case 2: Render a card with a popover to select, not only the category, but also the specific mode
  const renderCardContent = (transportOption: TransportOption) =>
    transportOption.modes?.length > 1 ? (
      <ModesOptionsPopover
        key={transportOption.category}
        transportOption={transportOption}
        onSelect={(e, mode) => handleSelectCategoryAndMode(e, { category: transportOption.category, mode })}
        onOpenChange={(open) => {
          if (!open) {
            // Give focus back to current card
            modeCardRef.current?.focus()
          }
        }}
      >
        {renderButtonCard(transportOption)}
      </ModesOptionsPopover>
    ) : (
      renderButtonCard(transportOption, {
        // Select the category if we don't have any modes options. Unless we have only
        // one mode option we select it by default
        onClick: (e, category) =>
          transportOption.modes?.length === 1
            ? handleSelectCategoryAndMode(e, { category, mode: transportOption.modes[0].mode })
            : handleSelectCategory(e, category)
      })
    )

  const selectedCategory = React.useMemo(
    () => findKey(modesSectionMapping, (modes) => modes.some((mode) => mode.name === currentMode)),
    [currentMode]
  )

  const memoValue = React.useMemo(
    () => ({ onSelect: handleSelectCategory, showDropdownOptionsCount }),
    [handleSelectCategory, showDropdownOptionsCount]
  )
  return (
    // Only the mode images are lazy loaded so we simply wrap the whole component in a suspense
    <ModeCardsSelectorContext.Provider value={memoValue}>
      <SelectableCardGrid
        id={id}
        equalWidth={equalWidth}
        cardSize={cardSize}
        baseWidth={baseWidth}
        selectedCards={currentMode ? selectedCategory : currentCategory || undefined}
        initialSelectedCards={currentMode ? selectedCategory : currentCategory || undefined}
        className={cx(styles['mode-selector'], 'app-mode-selector', className)}
      >
        {transportOptions.map(renderCardContent)}
        {children}
      </SelectableCardGrid>
    </ModeCardsSelectorContext.Provider>
  )
}

function DriveAndTransit() {
  const { onSelect, showDropdownOptionsCount } = useModeCardsSelectorContext()
  const handleClick = (e: React.MouseEvent) => {
    onSelect(e, CombinedModes.DriveAndTransit)
  }
  const { title } = getTranslationKeyForCategory('driveAndTransit')
  const intl = useIntl()
  return (
    <div className="u-flex-column u-flex-1">
      <ClickableCard
        id={CombinedModes.DriveAndTransit}
        cover={
          <span className="u-margin--medium u-margin-bottom--none">
            <Suspense fallback={<Skeleton.Image style={{ height: '72px', width: '115px' }} />}>
              <CustomCommuteIllustration height={72} />
            </Suspense>
          </span>
        }
        title={<FormattedMessage id={title} />}
        contentStyle={cardButtonStyle}
        onClick={handleClick}
      />
      {showDropdownOptionsCount && (
        <ModeOptions>
          {intl.formatMessage({ id: 'surveys.questions.types.commuteRouter.options.description.driveAndTransit' })}
        </ModeOptions>
      )}
    </div>
  )
}

function MixedModes() {
  const { onSelect, showDropdownOptionsCount } = useModeCardsSelectorContext()
  const handleClick = (e: React.MouseEvent) => {
    onSelect(e, CombinedModes.MixedModes)
  }
  const { title, description } = getTranslationKeyForCategory('multiple')
  const intl = useIntl()
  return (
    <div className="u-flex-column u-flex-1">
      <ClickableCard id={CombinedModes.MixedModes} onClick={handleClick}>
        <div className="u-flex u-padding--medium">
          <div className="u-flex u-flex-align-center u-padding-horizontal--small">
            <Suspense fallback={<Skeleton.Image style={{ height: '28px', width: '54px' }} />}>
              <SwirlIllustration width={28} />
            </Suspense>
          </div>
          <div className="u-padding-left--medium u-flex-column u-flex-align-start">
            <div
              className={cx(
                'u-color--neutral-600',
                'u-font-weight-semi u-margin-vertical--tiny',
                styles['multiple-mode-text-container']
              )}
            >
              <FormattedMessage id={title} />
            </div>
            {description ? (
              <div className={styles['multiple-mode-text-container']}>
                <FormattedMessage id={description} />
              </div>
            ) : null}
          </div>
        </div>
      </ClickableCard>
      {showDropdownOptionsCount && (
        <ModeOptions>
          {intl.formatMessage({ id: 'surveys.questions.types.commuteRouter.options.description.custom' })}
        </ModeOptions>
      )}
    </div>
  )
}

ModeCardsSelector.MixedModes = MixedModes
ModeCardsSelector.DriveAndTransit = DriveAndTransit
export default ModeCardsSelector
